import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Row, Col, Select, Progress, Button } from 'antd';
import { ReactComponent as ArrowDownIcon } from '../../icons/arrow-down.svg';
import NewSearchInput from '../../UI/inputs/NewSearchInput';
import SelectInput from '../../UI/inputs/Select';
import FormItem from '../form/FormItem';
import {
    ManagementForm,
    ActionButton,
    StyledDownloadIcon
} from './SharedStyledComponents';
import { exportJobAssignmentsToCSV, cancelExport } from '../../utils/jobAssignmentsExport';
import { FilterOptions, Filters } from '../../hooks/useJobAssignments';
import { useAppSelector } from '../../store/hooks';
import styled from 'styled-components';

const { Option } = Select;

const ExportContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
  padding: 0.5rem;
  background: #f6f8fa;
  border-radius: 4px;
  border: 1px solid #dae5ec;
`;

const ProgressText = styled.div`
  font-size: 0.75rem;
  color: #627d95;
  margin-bottom: 0.25rem;
`;

const ProgressStats = styled.div`
  font-size: 0.7rem;
  color: #627d95;
  margin-top: 0.25rem;
`;

const CancelButton = styled(Button)`
  && {
    background: #ff6b35;
    border-color: #ff6b35;
    color: white;
    font-size: 0.75rem;
    height: 24px;
    padding: 0 8px;
    
    &:hover {
      background: #e55a2b;
      border-color: #e55a2b;
    }
  }
`;

interface ExportProgress {
    currentPage: number;
    totalPages: number;
    recordsCount: number;
    timeRemaining: string;
}

interface JobAssignmentsFiltersProps {
    filterOptions: FilterOptions;
    filterOptionsLoading: boolean;
    onFilter: (_: any, allValues: any) => void;
    filters: Filters;
    // Add assignments data to enable proper job filtering
    allAssignmentsData?: Array<{
        job_title: string;
        job_company_name: string;
    }>;
}

const JobAssignmentsFilters: React.FC<JobAssignmentsFiltersProps> = ({
    filterOptions,
    filterOptionsLoading,
    onFilter,
    filters,
    allAssignmentsData = [],
}) => {
    const [t] = useTranslation();
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);
    const [form] = Form.useForm();
    const [selectedClientCompany, setSelectedClientCompany] = useState<string | undefined>(filters.client_company);

    // Get current user's company information
    const { user } = useAppSelector((state) => state.user);
    const currentUserCompanyName = user?.company?.name;

    // Watch for changes in form values to handle client company selection
    useEffect(() => {
        const subscription = form.getFieldsValue();
        if (subscription.client_company !== selectedClientCompany) {
            setSelectedClientCompany(subscription.client_company);
        }
    }, [form, selectedClientCompany]);

    const handleExport = async () => {
        setIsExporting(true);
        setExportProgress(null);

        try {
            await exportJobAssignmentsToCSV(
                filters,
                t,
                (current, total, recordsCount, timeRemaining) => {
                    setExportProgress({
                        currentPage: current,
                        totalPages: total,
                        recordsCount,
                        timeRemaining
                    });
                }
            );
        } finally {
            setIsExporting(false);
            setExportProgress(null);
        }
    };

    const handleCancelExport = () => {
        cancelExport();
        setIsExporting(false);
        setExportProgress(null);
    };

    const handleFormChange = (changedFields: any, allValues: any) => {
        // Check if client_company field changed
        const clientCompanyField = changedFields.find((field: any) => field.name && field.name[0] === 'client_company');

        if (clientCompanyField) {
            // If client company changed, reset job_title field
            form.setFieldsValue({ job_title: undefined });
            setSelectedClientCompany(clientCompanyField.value);

            // Update the allValues to reflect the reset job_title
            const updatedAllValues = allValues.map((field: any) => {
                if (field.name && field.name[0] === 'job_title') {
                    return { ...field, value: undefined };
                }
                return field;
            });

            onFilter(changedFields, updatedAllValues);
        } else {
            onFilter(changedFields, allValues);
        }
    };

    const getProgressPercentage = () => {
        if (!exportProgress) return 0;
        return Math.round((exportProgress.currentPage / exportProgress.totalPages) * 100);
    };

    // Filter out current user's company from client companies
    const getFilteredClientCompanies = () => {
        if (!currentUserCompanyName) {
            return filterOptions.client_companies;
        }

        return filterOptions.client_companies.filter(
            company => company !== currentUserCompanyName
        );
    };

    // Filter job titles based on selected client company
    const getFilteredJobTitles = () => {
        if (!selectedClientCompany) {
            // If no client company selected, show all job titles
            return filterOptions.job_titles;
        }

        // If we have assignments data, use it to filter job titles by company
        if (allAssignmentsData.length > 0) {
            const jobTitlesForCompany = allAssignmentsData
                .filter(assignment => assignment.job_company_name === selectedClientCompany)
                .map(assignment => assignment.job_title);

            // Remove duplicates and sort
            const uniqueJobTitles = Array.from(new Set(jobTitlesForCompany)).sort();
            return uniqueJobTitles;
        }

        // Fallback: if no assignments data available, show all (with warning in console)
        console.warn('No assignments data available for job title filtering. Showing all job titles.');
        return filterOptions.job_titles;
    };

    const filteredClientCompanies = getFilteredClientCompanies();
    const hasOtherCompanies = filteredClientCompanies.length > 0;
    const filteredJobTitles = getFilteredJobTitles();
    const hasFilteredJobs = selectedClientCompany && filteredJobTitles.length > 0;
    const hasNoJobsForCompany = selectedClientCompany && filteredJobTitles.length === 0;

    return (
        <ManagementForm>
            <Form
                form={form}
                scrollToFirstError={true}
                layout="horizontal"
                onFieldsChange={handleFormChange}
                initialValues={{
                    search: filters.search || undefined,
                    // Don't set default status - let it be empty to show both active and paused
                    ...(filters.status && { status: filters.status }),
                    // Don't set undefined values for selects - let them be truly undefined for placeholders
                    ...(filters.recruiter_company && { recruiter_company: filters.recruiter_company }),
                    ...(filters.client_company && hasOtherCompanies && { client_company: filters.client_company }),
                    ...(filters.job_title && { job_title: filters.job_title }),
                    aggregateByCompany: filters.aggregateByCompany || false,
                }}
            >
                <Row justify="space-between" gutter={10} wrap={false}>
                    <Row justify="start" gutter={10}>
                        <Col>
                            <FormItem name="search">
                                <NewSearchInput
                                    placeholder={t('SEARCH_ASSIGNMENTS', 'Search agencies, jobs...')}
                                    style={{ width: '400px' }}
                                />
                            </FormItem>
                        </Col>
                        <Col>
                            <FormItem>
                                <ExportContainer>
                                    {!isExporting ? (
                                        <ActionButton
                                            size="middle"
                                            onClick={handleExport}
                                        >
                                            <StyledDownloadIcon />
                                            {t('EXPORT', 'Export')}
                                        </ActionButton>
                                    ) : (
                                        <ProgressContainer>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <ProgressText>
                                                    📤 {t('EXPORTING', 'Exporting...')}
                                                </ProgressText>
                                                <CancelButton
                                                    size="small"
                                                    onClick={handleCancelExport}
                                                    title={t('CANCEL_EXPORT', 'Cancel export')}
                                                >
                                                    ✕
                                                </CancelButton>
                                            </div>
                                            {exportProgress && (
                                                <>
                                                    <Progress
                                                        percent={getProgressPercentage()}
                                                        size="small"
                                                        showInfo={false}
                                                        strokeColor="#34ce72"
                                                    />
                                                    <ProgressStats>
                                                        📄 Page {exportProgress.currentPage} of {exportProgress.totalPages} •
                                                        📊 {exportProgress.recordsCount.toLocaleString()} records •
                                                        ⏱️ {exportProgress.timeRemaining} left
                                                    </ProgressStats>
                                                </>
                                            )}
                                        </ProgressContainer>
                                    )}
                                </ExportContainer>
                            </FormItem>
                        </Col>
                    </Row>
                </Row>
                <Row justify="start" gutter={10}>
                    <Col xs={{ span: 12 }} md={{ span: 6 }} xl={{ span: 5 }}>
                        <FormItem name="status">
                            <SelectInput
                                showSearch
                                placeholder={t('STATUS', 'Status')}
                                optionFilterProp="children"
                                filterOption={true}
                                dropdownMatchSelectWidth={false}
                                className="custom-select"
                                suffixIcon={<ArrowDownIcon />}
                                allowClear={true}
                                disabled={false}
                            >
                                <Option value="active">{t('ACTIVE', 'Active')}</Option>
                                <Option value="paused">{t('PAUSED', 'Paused')}</Option>
                            </SelectInput>
                        </FormItem>
                    </Col>
                    {/* Only show client company filter if there are other companies */}
                    {hasOtherCompanies && (
                        <Col xs={{ span: 12 }} md={{ span: 6 }} xl={{ span: 5 }}>
                            <FormItem name="client_company">
                                <SelectInput
                                    showSearch
                                    placeholder={t('CLIENT_COMPANY', 'Client Company')}
                                    optionFilterProp="children"
                                    filterOption={true}
                                    dropdownMatchSelectWidth={false}
                                    className="custom-select"
                                    suffixIcon={<ArrowDownIcon />}
                                    allowClear
                                    loading={filterOptionsLoading}
                                >
                                    {filteredClientCompanies.map((company) => (
                                        <Option key={company} value={company}>
                                            {company}
                                        </Option>
                                    ))}
                                </SelectInput>
                            </FormItem>
                        </Col>
                    )}
                    <Col xs={{ span: 12 }} md={{ span: 6 }} xl={{ span: 5 }}>
                        <FormItem name="recruiter_company">
                            <SelectInput
                                showSearch
                                placeholder={t('RECRUITER_COMPANY', 'Agency')}
                                optionFilterProp="children"
                                filterOption={true}
                                dropdownMatchSelectWidth={false}
                                className="custom-select"
                                suffixIcon={<ArrowDownIcon />}
                                allowClear
                                loading={filterOptionsLoading}
                            >
                                {filterOptions.recruiter_companies.map((company) => (
                                    <Option key={company} value={company}>
                                        {company}
                                    </Option>
                                ))}
                            </SelectInput>
                        </FormItem>
                    </Col>
                    <Col xs={{ span: 12 }} md={{ span: 6 }} xl={{ span: 5 }}>
                        <FormItem name="job_title">
                            <SelectInput
                                showSearch
                                placeholder={
                                    hasNoJobsForCompany
                                        ? t('NO_JOBS_FOR_COMPANY', `No jobs for ${selectedClientCompany}`)
                                        : hasFilteredJobs
                                            ? t('JOB_TITLE_FILTERED', `${filteredJobTitles.length} jobs for ${selectedClientCompany}`)
                                            : t('JOB_TITLE', 'Job Title')
                                }
                                optionFilterProp="children"
                                filterOption={true}
                                dropdownMatchSelectWidth={false}
                                className="custom-select"
                                suffixIcon={<ArrowDownIcon />}
                                allowClear
                                loading={filterOptionsLoading}
                                disabled={!!hasNoJobsForCompany}
                            >
                                {filteredJobTitles.map((title) => (
                                    <Option key={title} value={title}>
                                        {title.length > 40 ? `${title.substring(0, 40)}...` : title}
                                    </Option>
                                ))}
                            </SelectInput>
                        </FormItem>
                    </Col>
                    {/* TODO: Uncomment when grouping feature is ready */}
                    {/* 
                    <Col xs={{ span: 12 }} md={{ span: 6 }} xl={{ span: 4 }}>
                        <FormItem name="aggregateByCompany" valuePropName="checked" label=" ">
                            <StyledCheckboxInput>
                                {t('AGGREGATE_BY_COMPANY', 'Group by agency')}
                            </StyledCheckboxInput>
                        </FormItem>
                    </Col>
                    */}
                </Row>
            </Form>
        </ManagementForm>
    );
};

export default JobAssignmentsFilters; 